<script setup>
import { Tooltip } from "bootstrap";
import { computed } from "vue";
import {
  commonMarkToHtml,
  formatUsername,
  getUserNameAndFallbacksFromRawData,
  processCitations,
  stripWhitespaceCharacters,
  urlify,
} from "@/utils/utils.js";
import InteractionStatusDropdown from "@/components/InteractionStatusDropdown.vue";
import IconFeedback from "@/components/ui-kit/base/IconFeedback.vue";
import api from "@/api";
import Icon from "../components/icons/Icon.vue";
import IconEmail from "@/components/icons/IconEmail.vue";
import IconSMS from "@/components/icons/IconSMS.vue";
import IconWhatsapp from "@/components/icons/IconWhatsapp.vue";
import IconSlack from "@/components/icons/IconSlack.vue";
import IconMeta from "@/components/icons/IconMeta.vue";
import IconMSTeams from "@/components/icons/IconMSTeams.vue";
import IconWordpress from "@/components/icons/IconWordpress.vue";
import IconChrome from "@/components/icons/IconChrome.vue";
import IconPencil from "@/components/icons/IconPencil.vue";
import IconGoogleChat from "@/components/icons/IconGoogleChat.vue";
import { useUserStore } from "@/stores/user";
import { useDepartmentStore } from "@/stores/department";
import { useInteractionDetailStore } from "@/stores/interactionDetail";
import { useOrganizationStore } from "@/stores/organization";
import { TAB_VALUE } from "@/constants/interaction";
import S3 from "@/utils/s3";

import {
  InteractionChannelEnum,
  InteractionStatus2Enum,
  InteractionSourceEnum,
  statusToOptionsMapping,
} from "@/utils/constants";
import StatusBadgeReadOnly from "./StatusBadgeReadOnly.vue";
import { useThreadsContextStore } from "@/stores/threads";
import { useWfbChatWidgetStore } from "@/stores/wfbChatWidget";
import { useInteractionTrainingStore } from "@/stores/interactionTraining";
import { useInteractionMedia } from "@/composables/useInteractionMedia"

const THUMBNAIL_WIDTH = "9.5em";

const props = defineProps({
  interaction: {
    type: Object,
    default: {},
  },
  active: {
    type: Boolean,
    default: false,
  },
  showTrainingButton: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String,
    default: "normal"
  },
});

const userStore = useUserStore();
const departmentStore = useDepartmentStore();
const interactionDetailStore = useInteractionDetailStore();
const interactionTrainingStore = useInteractionTrainingStore();
const wfbChatWidgetStore = useWfbChatWidgetStore();
const organizationStore = useOrganizationStore();
const threadContextStore = useThreadsContextStore()
const { mediaObjs } = useInteractionMedia(computed(() => props.interaction))

const cardComponentRef = ref(null);
const interactionRef = ref(props.interaction);
const isReadOnly = computed(() => props.interaction.isReadOnlyMode || true)
const interaction = computed(() => {
  if(props.interaction.thread_id && threadContextStore.getThreadById(props.interaction.thread_id)) {
    return interactionRef.value
  }
   

  return props.interaction._id === interactionDetailStore.interaction?._id
    ? interactionDetailStore.interaction
    : interactionRef.value

  }
);

const shortIdDisplay = computed(() => {
  const thread = threadContextStore.getThreadById(props.interaction.thread_id)

  if(!thread?.short_id) return props.interaction.short_id

  return `${thread.short_id}.${props.interaction.short_id}`
})

// Need this computed value bc we process citations on the response
const botResponseMessage = computed(()=>{
  const { response, citations } = props.interaction
  const html = formatMarkdown(response)
  return citations ? processCitations(html, citations) : html
})
const botResponseEl = ref(null)

watch(botResponseEl, async (newVal)=>{
  // need to wait till next tick, because updatedHtml has not reached the dom yet
  await nextTick()
  // if not null, that means the ele has appeared in the dom
  if(newVal) {
    newVal.querySelectorAll('[data-bs-toggle="tooltip"]')
    .forEach(el=>{
      // Dont update el, that already have _tooltip
      if(!el._tooltip) {
        el._tooltip = new Tooltip(el, {trigger: 'hover'})
      }
    })
  }
})
const userShortName = computed(() => {
  const name = getUserNameAndFallbacksFromRawData(
    userStore.allDict[interaction.value.user_id]
  );
  return formatUsername(name);
});

const lastModifyByShortName = computed(() => {
  const name = getUserNameAndFallbacksFromRawData(
    userStore.allDict[interaction.value.last_modify_by]
  );
  return formatUsername(name);
});

const filteredStatusDropdownOptions = computed(() => {
  return statusToOptionsMapping[interaction.value.status];
});

const channelIcon = computed(() => {
  switch (interaction.value.channel.toLowerCase()) {
    case InteractionChannelEnum.EMAIL:
      return IconEmail;
    case InteractionChannelEnum.SMS:
      return IconSMS;
    case InteractionChannelEnum.WHATSAPP:
      return IconWhatsapp;
    case InteractionChannelEnum.SLACK:
      return IconSlack;
    case InteractionChannelEnum.META:
      return IconMeta;
    case InteractionChannelEnum.MSTEAMS:
      return IconMSTeams;
    case InteractionChannelEnum.WORDPRESS:
      return IconWordpress;
    case InteractionChannelEnum.WEB:
      return IconChrome;
    case InteractionChannelEnum.WEBFORM:
      return IconPencil;
    case InteractionChannelEnum.GOOGLECHAT:
      return IconGoogleChat;
    default:
      return null;
  }
});

const downloadAttachment = async (attachment) => {
  const interaction_id = interaction.value.id;
  const bucket_name = attachment.bucket;
  const file_key = attachment.s3_key;

  const params = {
    interaction_id,
    bucket_name,
    file_key,
  };

  try {
    // Note that downloadLink is a redirect URL, so download name is set in backend.
    const res = await api.interactions.getAttachmentDownloadLink(params);
    const downloadLink = res.data;

    const fakeLink = document.createElement("a");
    fakeLink.href = downloadLink;
    fakeLink.target = "_blank";
    fakeLink.click();
  } catch (error) {
    console.error(
      `Error fetching download link for attachment: ${attachment.file_name}: `,
      error
    );
  }
};

const updateInteractionStatus = async (status) => {
  if (interaction.value._id === interactionDetailStore.interaction._id) {
    await interactionDetailStore.updateStatus(status);
  } else {
    interactionRef.value = await api.interactions.updateInteraction(
      interaction.value._id,
      { status }
    );
  }
};

const formatMarkdown = (message) => {
  return stripWhitespaceCharacters(commonMarkToHtml(message));
};

const openTraining = () => {
  wfbChatWidgetStore.closeWidget();
  interactionTrainingStore.setActiveInteraction(props.interaction.id)
}

const usesDialectTraining = computed(() => {
  return (
    props.showTrainingButton &&
    userStore.canCreateDialects &&
    organizationStore.myOrgEnablements.includes("agentic.adoption")
  );
});

const trainingForInteraction = computed(() => {
  return interactionTrainingStore.trainings[props.interaction.id];
});

const isUserApproverForInteraction = computed(() => {
  return interactionTrainingStore.isUserApprover(props.interaction.id);
});

onBeforeMount(() => {
  if (!usesDialectTraining.value) {
    interactionTrainingStore.load(props.interaction.id)
  }
})
</script>

<template>
  <div
    class="interaction-communication-card cursor-pointer"
    :class="{ active }"
    ref="cardComponentRef"
  >
    <div class="d-flex justify-content-between">
      <span class="text-neutral-350 font-size-xs">
        #{{ shortIdDisplay }}
      </span>

      <div class="d-flex align-items-center column-gap-2">
        <!-- Allow parent to replace this component with their own -->
        <slot name="top-right-corner" :interaction="interaction">
          <InteractionStatusDropdown
            v-if="!isReadOnly"
            @updated="updateInteractionStatus"
            :status="
              interaction._id == interactionDetailStore.interaction._id
                ? interactionDetailStore.interaction.status
                : interaction.status
            "
            :options="filteredStatusDropdownOptions"
            :allowToOpen="true"
          />
          <StatusBadgeReadOnly
            v-else
            :status="
              interaction._id == interactionDetailStore.interaction._id
                ? interactionDetailStore.interaction.status
                : interaction.status
            "
          />
        </slot>
      </div>
    </div>

    <!-- Direct Ticket interaction from WebForm -->
    <div
      v-if="
        interaction.source === InteractionSourceEnum.DIRECT_TICKET &&
        interaction.channel === InteractionChannelEnum.WEBFORM
      "
      class="my-mt-4"
    >
      <div class="message-left">
        <div
          class="message-text p-2 rounded"
          :class="{ small: size === 'small' }"
          v-html="commonMarkToHtml(interaction.query)"
        >
        </div>

        <div
          v-if="interaction.attachments?.length > 0"
          class="d-flex align-items-start column-gap-2 py-1 px-2 flex-column font-size-xs attachments-container"
        >
          <div class="font-size-xs text-neutral-300">
            Attachments
          </div>
          <div v-for="(attachment, index) in interaction.attachments" :key="index">
            <a
              class="attachment-link"
              href="#"
              @click.stop.prevent="() => downloadAttachment(attachment)"
            >
              {{ attachment.file_name }}
            </a>
          </div>
        </div>

        <div class="d-flex align-items-center mt-2">
          <user-avatar
            theme="neutral"
            :short-name="userShortName"
            :tooltip="
              getUserNameAndFallbacksFromRawData(
                userStore.allDict[interaction.user_id]
              )
            "
          >
            <template #icon>
              <IconMarker v-if="channelIcon">
                <component
                  :is="channelIcon"
                  :width="12"
                  :height="12"
                  v-b-tooltip.hover.top="`Via ${interaction.channel}`"
                />
              </IconMarker>
            </template>
          </user-avatar>
          <span class="ms-3 font-size-xs text-neutral-300">
            {{ interaction._createdAt_formatted_full_str }}
          </span>
        </div>
      </div>
    </div>

    <!-- Direct Ticket interaction from Support Personnel -->
    <div
      v-else-if="
        interaction.source === InteractionSourceEnum.DIRECT_TICKET &&
        interaction.channel === InteractionChannelEnum.WEB
      "
      class="my-mt-4"
    >
      <div class="message-left">
        <div class="d-flex align-items-center mt-2">
          <user-avatar
            theme="neutral"
            :short-name="userShortName"
            :tooltip="
              getUserNameAndFallbacksFromRawData(
                userStore.allDict[interaction.user_id]
              )
            "
          >
            <template #icon>
              <IconMarker v-if="channelIcon">
                <component
                  :is="channelIcon"
                  :width="12"
                  :height="12"
                  v-b-tooltip.hover.top="`Via ${interaction.channel}`"
                />
              </IconMarker>
            </template>
          </user-avatar>
          <span class="ms-3 font-size-xs text-neutral-300">
            Direct Ticket
          </span>
        </div>
      </div>

      <div class="message-right d-grid mt-4_5">
        <div
          class="message-text p-2 rounded"
          :class="{ small: size === 'small' }"
          v-html="commonMarkToHtml(interaction.query)"
        >
        </div>


        <div
          v-if="interaction.attachments?.length > 0"
          class="d-flex align-items-start column-gap-2 py-1 px-2 flex-column font-size-xs attachments-container"
        >
          <div class="font-size-xs text-neutral-300">
            Attachments
          </div>
          <div v-for="(attachment, index) in interaction.attachments" :key="index">
            <a
              class="attachment-link"
              href="#"
              @click.stop.prevent="() => downloadAttachment(attachment)"
            >
              {{ attachment.file_name }}
            </a>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-end mt-2">
          <span class="me-3 font-size-xs text-neutral-300">
            {{ interaction._createdAt_formatted_full_str }}
          </span>

          <user-avatar
            tooltip-semibold
            theme="white"
            :short-name="lastModifyByShortName"
            :tooltip="
              getUserNameAndFallbacksFromRawData(
                userStore.allDict[interaction.last_modify_by]
              )
            "
          />
        </div>
      </div>
    </div>

    <!-- standard user-prompted interaction -->
    <div v-else-if="interaction.is_user_prompt" class="my-mt-4">
      <div class="message-left">
        <div
          class="message-text p-2 rounded"
          :class="{ small: size === 'small' }"
          v-html="commonMarkToHtml(interaction.query)"
        >
        </div>

        <div
          v-if="interaction.attachments?.length > 0"
          class="d-flex align-items-start column-gap-2 py-1 px-2 flex-column font-size-xs attachments-container"
        >
          <div class="font-size-xs text-neutral-300">
            Attachments
          </div>
          <div v-for="(attachment, index) in interaction.attachments" :key="index">
            <a
              class="attachment-link"
              href="#"
              @click.stop.prevent="() => downloadAttachment(attachment)"
            >
              {{ attachment.file_name }}
            </a>
          </div>
        </div>

        <div class="d-flex align-items-center mt-2">
          <user-avatar
            theme="neutral"
            :short-name="userShortName"
            :tooltip="
              getUserNameAndFallbacksFromRawData(
                userStore.allDict[interaction.user_id]
              )
            "
          >
            <template #icon>
              <IconMarker v-if="channelIcon">
                <component
                  :is="channelIcon"
                  :width="12"
                  :height="12"
                  v-b-tooltip.hover.top="`Via ${interaction.channel}`"
                />
              </IconMarker>
            </template>
          </user-avatar>
          <span class="ms-3 font-size-xs text-neutral-300">
            {{ interaction._createdAt_formatted_full_str }}
          </span>
        </div>
      </div>

      <div class="message-right d-grid">
        <div class="message-text p-2 rounded"
        :class="{ small: size === 'small' }"
        >
          <div
            class="bot-response-content"
            ref="botResponseEl"
            v-html="botResponseMessage"
          >
          </div>
        <div v-if="mediaObjs.length" class="media-urls-container mt-2">
          <ImageThumbnail
            v-for="mediaObj in mediaObjs"
            :key="mediaObj.url"
            :imageObj="mediaObj"
            :width=THUMBNAIL_WIDTH
            @error="refresh"
          />
        </div>

    </div>

        <div class="d-flex align-items-center justify-content-end mt-2">
          <div
            v-if="interaction.confidence_score"
            class="d-flex align-items-center"
          >
            <IconConfidenceRobot class="me-1" />
            <span class="font-size-xs text-neutral-300 me-3">
              {{
                Math.min(100, interaction.confidence_score * 100).toFixed(1)
              }}% Confidence
            </span>
          </div>
          <span class="me-3 font-size-xs text-neutral-300">
            {{ interaction._createdAt_formatted_full_str }}
          </span>

          <user-avatar
            tooltip-semibold
            tooltip="AI Agent"
            short-name="AI"
            theme="white"
          />
        </div>
      </div>
    </div>

    <!-- every other type of interaction -->
    <div v-else class="my-mt-4">
      <div class="message-right d-grid">
        <div
          class="message-text p-2 rounded"
          v-html="formatMarkdown(interaction.query)"
        >
        </div>

        <div
          v-if="interaction.attachments?.length > 0"
          class="d-flex align-items-start column-gap-2 py-1 px-2 flex-column font-size-xs attachments-container"
        >
          <div class="font-size-xs text-neutral-300">
            Attachments
          </div>
          <div v-for="(attachment, index) in interaction.attachments" :key="index">
            <a
              class="attachment-link"
              href="#"
              @click.stop.prevent="() => downloadAttachment(attachment)"
            >
              {{ attachment.file_name }}
            </a>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-end mt-2">
          <div
            v-if="interaction.confidence_score"
            class="d-flex align-items-center"
          >
            <IconConfidenceRobot class="me-1" />
            <span class="font-size-xs text-neutral-300">
              {{
                Math.min(100, interaction.confidence_score * 100).toFixed(1)
              }}% Confidence
            </span>
          </div>
          <span class="ms-3 me-3 font-size-xs text-neutral-300">
            {{ interaction._createdAt_formatted_full_str }}
          </span>

          <user-avatar
            tooltip-semibold
            tooltip="AI Agent"
            short-name="AI"
            theme="white"
          />
        </div>
      </div>
    </div>

    <span v-if="interaction.has_thumbs_up">👍</span>
    <span v-if="interaction.has_thumbs_down">👎</span>
    <div
      v-if="
        interaction.has_feedback ||
        (interaction.has_ticket && interaction?.ticket_queries?.length > 0)
      "
      class="mt-4_5"
    >
      <div
        v-if="interaction.has_feedback"
        class="feedback-container feedback font-size-xs"
        v-for="feedback in interaction.feedback_queries"
      >
        <IconFeedback :height="16" :width="16" />
        <div v-if="typeof feedback === 'string'" class="feedback-text" v-b-tooltip.hover="feedback">
            {{ feedback }}
        </div>
      </div>
      <div
        v-if="
          interaction.has_ticket &&
          !interaction.is_ticket &&
          interaction?.ticket_queries?.length > 0
        "
        v-for="ticket in interaction.ticket_queries"
        class="feedback-container feedback font-size-xs"
      >
        <IconFeedback :height="16" :width="16" />
        <div v-if="typeof ticket === 'string'" class="feedback-text" v-b-tooltip.hover="ticket">
            {{ ticket }}
        </div>
      </div>
    </div>
    <div
      v-if="!usesDialectTraining && showTrainingButton"
      class="d-flex justify-content-start column-gap-3 mt-4_5">
      <TrainingCTALabel
        :isUserApprover="isUserApproverForInteraction"
        :training="trainingForInteraction"
        @onClick="openTraining"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">

$--min-width: 400px;
// $--max-width: 400px;
$--max-message-container-width: 28.75rem;
$--max-message-container-width-small: 100%;

.media-urls-container {
  display: flex;
  flex-wrap: wrap;
  // justify-content: center;
  gap: 0.5rem;
}

.interaction-communication-card {
  // links by default do not break, let text break and add min-width
  // width: 400px;
  min-width: 400px;
  overflow-wrap: anywhere;
  word-break: break-word;
  padding: 2rem;
  overflow: visible;
  flex-shrink: 0;
  border-bottom: 1px dashed $neutral-200;

  // TODO: remove this for the all interactions
  .mt-4_5 {
    margin-top: 2rem;
  }

  :deep(.icon-marker) {
    border-radius: 6px;
  }

  &.active {
    background-color: var(--primary-0);
    border: 1px dashed var(--primary-200);

    .feedback {
      padding: 4px;
      background-color: $neutral-0;
      border-color: $neutral-150;
      border-radius: 16px;
    }

    .message-right {
      .message-text {
        background-color: var(--primary-50) !important;
      }

      .media-images {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        justify-content: flex-end; // Align images to the right

        .media-image {
          img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            display: block;
          }
        }
      }
    }

    :deep(.status-dropdown) {
      .btn {
        background: $bluish-gray-100;
      }
    }
  }

  .message-text {
    width: fit-content;
    max-width: $--max-message-container-width;
    word-wrap: break-word;
    font-size: $font-size-sm;
    overflow-x: scroll;

    &.small {
      max-width: $--max-message-container-width-small;
    }
  }

  .message-left {
    .message-text {
      background-color: $neutral-100;
      color: $neutral-600;
      border-radius: 12px 12px 12px 0px !important;
      padding: 16px !important;
      
      &.small {
        padding-bottom: 0px !important;
        color: $neutral-350;
        background-color: $neutral-50;
      }
    }
  }

  .message-right {
    margin-top: 2rem;

    .message-text {
      justify-self: end;
      background-color: var(--primary-0);
      color: var(--primary-450);
      border-radius: 12px 12px 0px 12px !important;
      padding: 16px !important;
      
      &.small {
        color: $neutral-350;
        padding-bottom: 0px !important;
      }

      :deep(.bot-response-content .markdown),
      :deep(.bot-response-content .markdown *) {
        font-size: inherit !important;
        line-height: inherit !important;
      }
      // i have no idea why but overriding the markdown style for fontsize/line height somehow unbolds headers. 
      // im going to rebold for now unless shray tells me otherwise since i think its the expected look
      :deep(.bot-response-content .markdown h1),
      :deep(.bot-response-content .markdown h2),
      :deep(.bot-response-content .markdown h3),
      :deep(.bot-response-content .markdown h4),
      :deep(.bot-response-content .markdown h5),
      :deep(.bot-response-content .markdown h6),
      :deep(.bot-response-content .markdown strong),
      :deep(.bot-response-content .markdown b) {
        font-weight: bold !important;
      } 
    }
  }

  .media-images {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    .media-image img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }
  }

  .feedback {
    border: 1px solid $bluish-gray-600;
    background-color: white;
    border-radius: 12px;
  }

  .feedback-container {
    display: flex;
    align-items: center;
    padding: 3px;
    margin-top: 5px;
  }

  .tooltip-inner {
    max-width: 400px;
  }

  .feedback-text {
    margin-left: 0.5rem;
    max-width: 550px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .attachments-container {
    .attachment-link {
      overflow-wrap: break-word;
    }
  }
}
</style>
